import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Typography, Chip, Box, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Checkbox, FormControlLabel, ToggleButtonGroup, ToggleButton, LinearProgress,
} from '@mui/material';
import {
  Stop as StopIcon,
  PlayArrow as StartIcon,
  Replay as RetryIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { AdminListPanel } from './AdminListPanel';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';
import type { Job } from '../../api/types';

const STATUS_FILTERS = ['ALL', 'RUNNING', 'QUEUED', 'COMPLETED', 'FAILED'] as const;

const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 80 },
  {
    field: 'type',
    headerName: 'Type',
    width: 100,
    renderCell: (params) => (
      <Chip label={String(params.value ?? '—')} size="small" sx={{ fontSize: 10, height: 18 }} />
    ),
  },
  { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
  { field: 'user', headerName: 'User', width: 100 },
  {
    field: 'status',
    headerName: 'Status',
    width: 90,
    renderCell: (params) => {
      const s = String(params.value ?? '');
      const color = s === 'RUNNING' ? 'info' : s === 'COMPLETED' ? 'success' : s === 'FAILED' ? 'error' : s === 'QUEUED' ? 'warning' : 'default';
      return <Chip label={s || '—'} size="small" color={color} sx={{ fontSize: 10, height: 18 }} />;
    },
  },
  {
    field: 'progress',
    headerName: 'Progress',
    width: 100,
    renderCell: (params) => {
      const p = params.value as number | undefined;
      if (p == null) return <Typography variant="caption" sx={{ fontSize: 11 }}>—</Typography>;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
          <LinearProgress variant="determinate" value={p} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
          <Typography variant="caption" sx={{ fontSize: 10, minWidth: 28, textAlign: 'right' }}>{p}%</Typography>
        </Box>
      );
    },
  },
  { field: 'startTime', headerName: 'Started', width: 160,
    valueFormatter: (value: string | number | undefined) => value ? new Date(value).toLocaleString() : '—',
  },
  { field: 'endTime', headerName: 'Ended', width: 160,
    valueFormatter: (value: string | number | undefined) => value ? new Date(value).toLocaleString() : '—',
  },
];

/**
 * Jobs admin tab — JobListPanel equivalent.
 * Lists background processing jobs (transfer, CD burn, fax, etc.).
 * With multi-select, Stop/Start/Retry/Delete actions, auto-refresh, and status filter.
 */
export const JobsPanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() });
  const [confirmDialog, setConfirmDialog] = useState<{ action: string; fn: () => Promise<void> } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [detailJob, setDetailJob] = useState<Job | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data, isLoading } = useQuery<Job[]>({
    queryKey: queryKeys.jobs.all,
    queryFn: async () => {
      try {
        const result = await api.getJobs();
        return result.records ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 15_000,
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
  }, [queryClient]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(refresh, 15_000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refresh]);

  const jobs = useMemo(() => {
    let all = data ?? [];
    if (statusFilter !== 'ALL') {
      all = all.filter((j) => (j.status ?? '').toUpperCase() === statusFilter);
    }
    if (!search) return all;
    const s = search.toLowerCase();
    return all.filter(
      (j) =>
        j.id.toLowerCase().includes(s) ||
        j.type.toLowerCase().includes(s) ||
        (j.description ?? '').toLowerCase().includes(s) ||
        (j.user ?? '').toLowerCase().includes(s),
    );
  }, [data, search, statusFilter]);

  // Status counts
  const statusCounts = useMemo(() => {
    const all = data ?? [];
    const counts: Record<string, number> = { ALL: all.length };
    for (const j of all) {
      const st = (j.status ?? 'UNKNOWN').toUpperCase();
      counts[st] = (counts[st] ?? 0) + 1;
    }
    return counts;
  }, [data]);

  const selectedIds = useMemo(
    () => (selected.type === 'include' ? [...selected.ids] as string[] : []),
    [selected],
  );
  const hasSelection = selectedIds.length > 0;

  const runAction = useCallback(async (action: string, fn: () => Promise<unknown>) => {
    setConfirmDialog(null);
    try {
      await fn();
      pushSuccess(`${action} completed successfully`);
    } catch (e) {
      pushError(`${action} failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    refresh();
  }, [refresh]);

  const askConfirm = useCallback((action: string, fn: () => Promise<unknown>) => {
    setConfirmDialog({ action, fn: () => runAction(action, fn) });
  }, [runAction]);

  const toolbar = (
    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
      <Tooltip title="Stop selected jobs">
        <span>
          <IconButton size="small" disabled={!hasSelection} onClick={() => askConfirm('Stop', () => api.stopJobs(selectedIds))} sx={{ width: 28, height: 28 }}>
            <StopIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Start selected jobs">
        <span>
          <IconButton size="small" disabled={!hasSelection} onClick={() => askConfirm('Start', () => api.startJobs(selectedIds))} sx={{ width: 28, height: 28 }}>
            <StartIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Retry selected jobs">
        <span>
          <IconButton size="small" disabled={!hasSelection} onClick={() => askConfirm('Retry', () => api.retryJobs(selectedIds))} sx={{ width: 28, height: 28 }}>
            <RetryIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete selected jobs">
        <span>
          <IconButton size="small" disabled={!hasSelection} onClick={() => askConfirm('Delete', () => api.deleteJobs(selectedIds))} sx={{ width: 28, height: 28, color: hasSelection ? 'error.main' : undefined }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  return (
    <>
      <AdminListPanel
        title="Jobs"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        toolbar={toolbar}
        statusText={`${jobs.length} jobs${hasSelection ? ` · ${selectedIds.length} selected` : ''}`}
      >
        {/* Status filter bar + auto-refresh */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, gap: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size="small" value={statusFilter} exclusive
            onChange={(_, v) => { if (v) setStatusFilter(v); }}
            sx={{ height: 22 }}
          >
            {STATUS_FILTERS.map((sf) => (
              <ToggleButton key={sf} value={sf} sx={{ fontSize: 10, px: 1, py: 0, textTransform: 'none' }}>
                {sf === 'ALL' ? 'All' : sf.charAt(0) + sf.slice(1).toLowerCase()}
                {statusCounts[sf] != null ? ` (${statusCounts[sf]})` : ''}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <FormControlLabel
            control={<Checkbox size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} sx={{ p: 0.25 }} />}
            label={<Typography sx={{ fontSize: 10 }}>Auto-refresh (15s)</Typography>}
            sx={{ ml: 0 }}
          />
        </Box>

        <DataGrid
          rows={jobs}
          columns={columns}
          getRowId={(row) => (row as Job).id}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          checkboxSelection
          rowSelectionModel={selected}
          onRowSelectionModelChange={setSelected}
          onRowClick={(params) => setDetailJob(params.row as Job)}
          rowHeight={24}
          columnHeaderHeight={28}
          sx={{
            border: 'none',
            fontSize: 12,
            cursor: 'pointer',
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </AdminListPanel>

      {/* Confirm dialog */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs">
        <DialogTitle sx={{ fontSize: 14 }}>Confirm {confirmDialog?.action}</DialogTitle>
        <DialogContent sx={{ fontSize: 12 }}>
          {confirmDialog?.action} {selectedIds.length} job(s)?
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmDialog(null)} sx={{ textTransform: 'none', fontSize: 12 }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={confirmDialog?.fn} sx={{ textTransform: 'none', fontSize: 12 }}>
            {confirmDialog?.action}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job detail dialog */}
      {detailJob && (
        <Dialog open onClose={() => setDetailJob(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
          <DialogTitle sx={{ fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
            Job Details — {detailJob.id}
            <IconButton size="small" onClick={() => setDetailJob(null)} sx={{ width: 24, height: 24 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            <DR l="ID" v={detailJob.id} />
            <DR l="Type" v={detailJob.type} />
            <DR l="Description" v={detailJob.description ?? '—'} />
            <DR l="User" v={detailJob.user ?? '—'} />
            <DR l="Status" v={detailJob.status ?? '—'} />
            <DR l="Progress" v={detailJob.progress != null ? `${detailJob.progress}%` : '—'} />
            <DR l="Started" v={detailJob.startTime ? new Date(detailJob.startTime).toLocaleString() : '—'} />
            <DR l="Ended" v={detailJob.endTime ? new Date(detailJob.endTime).toLocaleString() : '—'} />
            {/* Show any extra fields from raw data */}
            {Object.entries(detailJob)
              .filter(([k]) => !['id', 'type', 'description', 'user', 'status', 'progress', 'startTime', 'endTime'].includes(k) && !k.startsWith('_'))
              .map(([k, v]) => (
                <DR key={k} l={k} v={String(v ?? '—')} />
              ))}
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 1.5 }}>
            <Button size="small" onClick={() => setDetailJob(null)} sx={{ textTransform: 'none', fontSize: 12 }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const DR = ({ l, v }: { l: string; v: string }) => (
  <Box sx={{ display: 'flex', mb: 0.5, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 90, textAlign: 'right', color: 'text.secondary' }}>{l}:</Typography>
    <Typography variant="body2" sx={{ fontSize: 12 }}>{v}</Typography>
  </Box>
);
